import i18n from '@/i18n'
import {ecologicalUnitService} from '@/services/ecological-unit.service'
import {enterpriseService} from '@/services/enterprise.service'
import {mapActions, mapGetters} from 'vuex'
import {EventHub, SnackbarStyle} from '@/constants/constants'
import {DialogEcologicalUnit, DialogFarm} from "@/tools/dialog"
import TreeSelectorComponent from '@/components/commons/tree-selector/TreeSelectorComponent.vue'
import {clone} from 'ramda'
import {farmService} from "@/services/farm.service";
import AutocompleteComponent from "@/components/commons/autocomplete/AutocompleteComponent.vue";

export default {
    name: 'EcologicalUnitDialogComponent',
    components: {
        TreeSelectorComponent,
        AutocompleteComponent
    },
    props: {
        // v-model injects the object to be edited into props.value
        model: {
            type: Object,
            required: true
        }
    },
    data: () => ({
        // model del formulario
        validForm: false,
        // reglas de validación
        validationRules: {
            required: (value) => !!value || `${i18n.t('required')}`,
        },
        // errores para el campo del núm de línea
        nameError: [],
        // model para el dialog
        showEditDialog: false,
        // título del dialog
        editDialogTitle: i18n.t('ecologicalUnit.editDialog.titleNew'),
        // normalizer para el treeselect
        normalizer: (node) => {
            return {
                id: node._id,
                label: node.name,
                children: node.children
            }
        },
        // empresas
        enterprises: [],
        // fincas
        farms: [],
        // nombre actual
        actualName: null,
        initFarmId: null
    }),
    computed: {
        ...mapGetters({
            'getBread': 'breadcrumbs/getBread',
        }),
    },
    created() {
        this.actualName = this.model.name
        // Evento lanzado por el diálogo de creación de finca, cuando se guarda la nueva finca
        this.$eventBus.$on(EventHub.COMMIT_FARM, (farmDto) => {
            this.loadFarms(farmDto.name);
        })
        /**
         * El modelo de datos del diálogo se carga a través del objeto del breadcrumb
         */
        this.unwatch = this.$store.watch(
            (state, getters) => getters['breadcrumbs/getBread'],
            (newBreads) => {
                const lastBreadcrumb = this.getBread[newBreads.length - 1]
                if (!lastBreadcrumb) {
                    this.cancel()
                } else {
                    if (lastBreadcrumb.key === DialogEcologicalUnit) {
                        if (lastBreadcrumb.model) {
                            const modelData = this.model
                            Object.assign(modelData, lastBreadcrumb.model)
                        }
                    }
                }
            },
            {deep: true})
    },
    beforeDestroy() {
        // desuscripción al bus de eventos
        this.$eventBus.$off(EventHub.COMMIT_FARM)
        // elimino la instancia del watcher del breadcrumb
        this.unwatch()
    },
    async mounted() {
        /**
         * El modelo de datos del diálogo se carga a través del objeto del breadcrumb
         */
        const lastBreadcrumb = this.getBread[this.getBread.length - 1]
        if (!lastBreadcrumb) {
            this.cancel()
        } else {
            if (lastBreadcrumb.key === DialogEcologicalUnit) {
                if (lastBreadcrumb.model) {
                    const modelData = this.model
                    Object.assign(modelData, lastBreadcrumb.model)
                }
            }
        }
        this.enterprises = await enterpriseService.getAllForSelector()
        this.loadFarms()
    },
    methods: {
        /**
         * actions del snackbar
         */
        ...mapActions('snackbar', [
            'showSnackbar',
            'closeSnackbar'
        ]),
        /**
         * actions del dialog
         */
        ...mapActions('dialog', [
            'openDialog',
            'closeDialog'
        ]),
        /**
         * actions del breadcrumb
         */
        ...mapActions('breadcrumbs', [
            'pullBread',
            'pushBread'
        ]),
        /**
         * Mensaje de error cuando el nombre está duplicado
         */
        async nameDuplicatedMethod() {
            if (this.model.name === this.actualName) {
                this.nameError = []
                return true
            }
            const response = await ecologicalUnitService.validateEcologicalUnitName(this.model.name, this.model.enterpriseId, this.model.farmId)
            if (response) {
                this.nameError.push(`${i18n.t('ecologicalUnit.duplicate')}`)
                return false
            } else {
                this.nameError = []
                return true
            }

        },
        /**
         * Resetea los campos del formulario de creación/actualización
         */
        resetFields() {
            if (this.$refs.form) {
                this.$refs.form.reset()
            }
        },
        /**
         * Click fuera del dialog
         */
        clickOutside() {
            this.cancel()
        },
        async loadFarms(farmId) {
            if (this.model.enterpriseId) {
                farmService.findForSelector(this.model.enterpriseId).then(response => {
                    this.farms = response;
                    if (farmId) {
                        this.model.farmId = farmId
                    }
                });
            }
        },
        /**
         * Cancelar
         */
        cancel() {
            this.pullBread(DialogEcologicalUnit)
            this.resetFields()
        },
        /**
         * Guardar
         */
        async save() {
            if (!await this.nameDuplicatedMethod()) return
            const ecologicalUnitDto = this.getEcologicalUnitDto(this.model.update)
            if (this.model.update) {
                ecologicalUnitService.update(ecologicalUnitDto).then(() => {
                    this.showSnackbar({
                        visible: true,
                        text: this.$t('ecologicalUnit.successUpdateMessage'),
                        timeout: 6000,
                        style: SnackbarStyle.SUCCESS
                    })
                    this.resetFields()
                    this.$emit('commitEcologicalUnit')
                    this.cancel()
                    this.$eventBus.$emit(EventHub.COMMIT_ECOLOGICAL_UNIT, ecologicalUnitDto)
                })
            } else {
                ecologicalUnitService.create(ecologicalUnitDto).then(() => {
                    this.showSnackbar({
                        visible: true,
                        text: this.$t('ecologicalUnit.successCreateMessage'),
                        timeout: 6000,
                        style: SnackbarStyle.SUCCESS
                    })
                    this.resetFields()
                    this.$emit('commitEcologicalUnit')
                    this.cancel()
                    this.$eventBus.$emit(EventHub.COMMIT_ECOLOGICAL_UNIT, ecologicalUnitDto)
                })
            }
        },
        /**
         * Genera el DTO para actualizar / crear una finca
         * @param {*} update
         */
        getEcologicalUnitDto(update = false) {
            const result = {
                name: this.model.name,
                enterpriseId: this.model.enterpriseId,
                farmId: this.model.farmId
            }
            if (update) {
                result.ecologicalUnitId = this.model.id
                result.initFarmId = this.initFarmId
                result._id = this.model._id
            }
            return result
        },
        /**
         * Carga el dialog para crear líneas
         */
        openFarm() {
            const bread = {
                key: DialogFarm,
                disabled: true,
                text: this.$t('farm.name'),
                model: {enterpriseId: this.model.enterpriseId, disableEnterpriseSelector: true}
            }
            this.pushBread(bread)
        },
        /**
         * Cada vez que se modifica la selección de la empresa, se cargan
         */
        enterpriseSelectedFunction (newValue) {
            this.model.enterpriseId = newValue._id ? newValue._id : newValue;
            this.loadFarms();
        },
        /**
         * Cada vez que se modifica la selección de la finca, se cargan
         */
        farmSelectedFunction (newValue) {
            this.model.farmId = newValue.id ? newValue.id : newValue;
        },
    },
    watch: {
        model: {
            deep: true,
            immediate: true,
            handler(newModel) {
                this.enterprises = clone(newModel.enterprises)
                this.initFarmId = newModel.farmId
                if (newModel.enterpriseId) {
                    this.loadFarms()
                }
            }
        },
    }
}
